/**
 * Design System — Color tokens.
 *
 * SINGLE VISUAL SOURCE OF TRUTH: `@tradelogic/ui-tokens` (per G05 Color Standard
 * + G06 CR-08 — "@tradelogic/ui-tokens = sole visual SoT"). The brand, neutral,
 * text, surface and border layers below are DERIVED from the canonical package
 * ramps + semantic layer, so the design-system can never fork from the tokens
 * again. Status colours (success/warning/danger/info) are AA-hardened variants
 * of the package hue family: the package's badge-tuned semantic status values do
 * not meet WCAG 4.5:1 as text-on-tint (MF-12), so darkened forms are used and
 * locked by TokenContrast.test.ts. Semantic names stay theme-agnostic so
 * light/dark and per-tenant overrides resolve the same variable names.
 *
 * NOTE: presentation-only. No business logic here.
 */
import { tokens } from '@tradelogic/ui-tokens';

const { brand, neutral, semantic } = tokens.color;

export interface SemanticColors {
  /** Primary brand / action color (buttons, links, active nav). */
  readonly brandPrimary: string;
  /** Hover / pressed state of the primary. */
  readonly brandPrimaryHover: string;
  /** Secondary / deep brand accent. */
  readonly brandSecondary: string;

  /** App page background. */
  readonly bgCanvas: string;
  /** Panels, cards, table surface. */
  readonly bgSurface: string;
  /** Elevated surface (menus, popovers). */
  readonly bgElevated: string;
  /** Subtle fills. */
  readonly bgMuted: string;
  /** Row / control hover background. */
  readonly bgHover: string;
  /** Selected table row background. */
  readonly bgSelected: string;
  /** Active sidebar menu background. */
  readonly bgActiveMenu: string;
  /** Modal scrim. */
  readonly bgOverlay: string;

  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly textMuted: string;
  readonly textInverse: string;
  readonly textLink: string;

  readonly borderDefault: string;
  readonly borderStrong: string;
  readonly borderFocus: string;

  readonly success: string;
  readonly successBg: string;
  readonly warning: string;
  readonly warningBg: string;
  readonly danger: string;
  readonly dangerBg: string;
  readonly info: string;
  readonly infoBg: string;
}

/** Light theme — brand/neutral/text/surface derived from @tradelogic/ui-tokens. */
export const lightColors: SemanticColors = {
  brandPrimary: brand[500],
  brandPrimaryHover: brand[600],
  brandSecondary: brand[700],

  bgCanvas: neutral[50],
  bgSurface: semantic.background,
  bgElevated: semantic.background,
  bgMuted: neutral[100],
  bgHover: neutral[100],
  bgSelected: brand[50],
  bgActiveMenu: brand[50],
  bgOverlay: 'rgba(15, 23, 42, 0.40)',

  textPrimary: semantic.textPrimary,
  textSecondary: semantic.textSecondary,
  textMuted: neutral[500],
  textInverse: semantic.textInverse,
  textLink: brand[600],

  borderDefault: semantic.border,
  borderStrong: neutral[300],
  borderFocus: semantic.focusRing,

  // AA-hardened status (see file header): hue-aligned to the package, darkened for 4.5:1 text-on-tint.
  success: '#1A7F37',
  successBg: '#EBF7EE',
  warning: '#A15C00',
  warningBg: '#FEF5EC',
  danger: '#BA0517',
  dangerBg: '#FDECEA',
  info: '#0B5CAB',
  infoBg: '#EAF3FB',
};

/** Dark theme — same canonical ramps (brand lightened, neutrals inverted). */
export const darkColors: SemanticColors = {
  brandPrimary: brand[400],
  brandPrimaryHover: brand[300],
  brandSecondary: brand[500],

  bgCanvas: neutral[900],
  bgSurface: neutral[800],
  bgElevated: neutral[700],
  bgMuted: neutral[800],
  bgHover: neutral[700],
  bgSelected: brand[900],
  bgActiveMenu: brand[900],
  bgOverlay: 'rgba(0, 0, 0, 0.60)',

  textPrimary: neutral[50],
  textSecondary: neutral[300],
  textMuted: neutral[400],
  textInverse: neutral[900],
  textLink: brand[300],

  borderDefault: neutral[700],
  borderStrong: neutral[600],
  borderFocus: brand[400],

  // AA-hardened status for dark surfaces (locked by TokenContrast.test.ts).
  success: '#45C65A',
  successBg: '#12331C',
  warning: '#FFB75D',
  warningBg: '#3A2A12',
  danger: '#FE5C4C',
  dangerBg: '#3A1512',
  info: '#57A8FF',
  infoBg: '#102438',
};
